import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuthV2 } from "@/lib/authv2/store";
import { logout as callLogout, syncStaffLink, type AuthError } from "@/lib/authv2/api";
import { ensureSessionRefreshed } from "@/lib/authv2/refreshInterceptor";
import { Button } from "@/ui/button";
import { BRAND_ART, BrandBand, BrandMark } from "@/components/BrandArt";
import { OrderPhotoUploadPopup } from "@/features/orders/components/OrderPhotoUploadPopup";
import { PersonaMenuSection } from "@/features/persona/PersonaMenuSection";
import { isLockedRole } from "@/lib/auth/roles";
import { roleLabel } from "@/lib/auth/role-labels";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

const SESSION_KEEPALIVE_INTERVAL_MS = 10 * 60 * 1000;
const SESSION_KEEPALIVE_MIN_GAP_MS = 5 * 60 * 1000;

export function AppLayout() {
  const accessToken = useAuthV2((s) => s.accessToken);
  const currentStaff = useAuthV2((s) => s.currentStaff);
  const displayName = useAuthV2((s) => s.displayName);
  const casdoorSub = useAuthV2((s) => s.casdoorSub);
  const isAuthenticated = Boolean(casdoorSub);
  const [hydrating, setHydrating] = useState(!isAuthenticated);
  const lastKeepAliveAt = useRef(0);

  // On first mount with no in-memory token, hydrate via /auth/refresh. The
  // HttpOnly session_id cookie survives page reloads even though the
  // accessToken (in-memory) doesn't. Go through ensureSessionRefreshed so this
  // shares ONE in-flight refresh with StrictMode's second effect invocation and
  // with the 401 interceptor — two concurrent /auth/refresh calls would rotate
  // the Casdoor refresh token twice, and the second (reused) rotation makes the
  // BE revoke every session for the sub, wiping the token AND the device PIN.
  // ensureSessionRefreshed also hydrates the store on success (casdoor_sub +
  // display_name + permissions), so no setSession call is needed here.
  useEffect(() => {
    // Đã có phiên trong bộ nhớ thì hydrating đã là false ngay từ state khởi
    // tạo (`!isAuthenticated`) — không cần set lại.
    if (isAuthenticated) return;
    let alive = true;
    ensureSessionRefreshed().finally(() => {
      if (alive) setHydrating(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cookie-session requests authenticate through the server-side snapshot,
  // which is intentionally short-lived. Keep it warm while the app is open so
  // polling screens do not all discover expiry via simultaneous 401s.
  useEffect(() => {
    if (!isAuthenticated) return;
    const keepAlive = () => {
      const now = Date.now();
      if (now - lastKeepAliveAt.current < SESSION_KEEPALIVE_MIN_GAP_MS) return;
      lastKeepAliveAt.current = now;
      void ensureSessionRefreshed();
    };
    const interval = window.setInterval(keepAlive, SESSION_KEEPALIVE_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") keepAlive();
    };
    window.addEventListener("focus", keepAlive);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", keepAlive);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated]);

  if (hydrating) {
    // Brand splash (mockup 28/09/2026, spot 7): the Chụp Ảnh ribbon with gold
    // foil running through it. Same wording as before.
    return (
      <div
        role="status"
        className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-brand)] text-[#FFFEF5]"
      >
        <span aria-hidden="true" className="relative h-[92px] w-[110px]">
          <BrandMark src={BRAND_ART.ribbon} className="absolute inset-0 text-[#FFFEF5]/25" />
          <span
            className="gn-foil-run absolute inset-0"
            style={{ ["--gn-src" as string]: `url(${BRAND_ART.ribbon})` }}
          />
        </span>
        <p className="m-0 mt-6 text-[15px] font-medium">Đang khôi phục phiên…</p>
        <BrandBand src={BRAND_ART.riceBand} height={9} className="mt-3 w-40 text-[#FFFEF5]/55" />
      </div>
    );
  }

  // No accessToken requirement: a PIN login under cookie-session leaves the
  // store without a Casdoor token, and gating on one bounced exactly the
  // unlinked accounts this screen exists for straight back to /login.
  if (isAuthenticated && !currentStaff) {
    return (
      <StaffLinkRequired
        accessToken={accessToken}
        displayName={displayName}
        casdoorSub={casdoorSub}
      />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Vai đã khoá: máy chủ từ chối mọi lượt gọi (CEO chốt Q02 17/10/2026), nên
  // không vẽ app — vẽ app là mười khối báo lỗi mà người dùng không hiểu vì sao.
  if (currentStaff && isLockedRole(currentStaff.role)) {
    return <LockedRoleScreen displayName={displayName} role={currentStaff.role} />;
  }

  return (
    <AppShell>
      <Outlet />
      {/* Mẻ nạp ảnh gốc của đơn chạy ở tầng app — hộp tiến trình theo người dùng sang mọi trang. */}
      <OrderPhotoUploadPopup />
    </AppShell>
  );
}

function StaffLinkRequired({
  accessToken,
  displayName,
  casdoorSub,
}: {
  accessToken: string | null;
  displayName: string | null;
  casdoorSub: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  async function handleSwitchAccount() {
    setBusy(true);
    try {
      await callLogout();
    } catch {
      // Best effort: local clear still removes the unusable app session.
    } finally {
      useAuthV2.getState().clear();
      window.location.assign("/login");
    }
  }

  async function handleSyncStaffLink() {
    setSyncing(true);
    setSyncError(null);
    try {
      const out = await syncStaffLink(accessToken);
      const state = useAuthV2.getState();
      state.setSession({
        accessToken,
        casdoorSub: state.casdoorSub ?? casdoorSub ?? "",
        displayName: state.displayName ?? displayName ?? out.staff.name,
        staff: out.staff,
        permissions: Array.from(state.permissions),
      });
    } catch (error) {
      setSyncError(syncStaffLinkMessage(error as AuthError));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-6 py-10">
      <section className="w-full max-w-lg rounded-lg border border-border bg-surface p-6 shadow-sm">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-lg font-semibold text-amber-800">
          !
        </div>
        <h1 className="text-2xl font-semibold text-text">Tài khoản chưa được cấp quyền</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {displayName ? `${displayName} đã đăng nhập Casdoor` : "Tài khoản này đã đăng nhập Casdoor"}, nhưng
          chưa được liên kết với hồ sơ nhân sự GNPF nên ứng dụng chưa thể xác định vai trò và phòng ban.
        </p>
        {casdoorSub && (
          <p className="mt-3 break-all rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Casdoor ID: {casdoorSub}
          </p>
        )}
        <p className="mt-4 text-sm leading-6 text-muted-foreground">
          Bấm đồng bộ để GNPF tự liên kết bằng email Casdoor nếu tìm thấy đúng một hồ sơ nhân sự phù hợp.
          Nếu không khớp, hãy liên hệ admin để kiểm tra email hoặc cấp quyền thủ công.
        </p>
        {syncError && (
          <p className="mt-4 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger-text">
            {syncError}
          </p>
        )}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={handleSwitchAccount} disabled={busy}>
            {busy ? "Đang xử lý…" : "Đổi tài khoản"}
          </Button>
          <Button type="button" onClick={handleSyncStaffLink} disabled={syncing || busy}>
            <RefreshCw className={syncing ? "animate-spin" : ""} />
            {syncing ? "Đang đồng bộ…" : "Đồng bộ tài khoản"}
          </Button>
        </div>
      </section>
    </main>
  );
}

async function switchAccount() {
  try {
    await callLogout();
  } catch {
    // Best effort: local clear still removes the unusable app session.
  } finally {
    useAuthV2.getState().clear();
    window.location.assign("/login");
  }
}

function LockedRoleScreen({ displayName, role }: { displayName: string | null; role: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-6 py-10">
      <section className="w-full max-w-lg rounded-lg border border-border bg-surface p-6 shadow-sm">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-lg font-semibold text-amber-800">
          !
        </div>
        <h1 className="text-2xl font-semibold text-text">Tài khoản đã khoá</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {displayName ?? "Tài khoản này"} đang mang vai «{roleLabel(role)}». Vai này đã khoá nên ứng dụng không mở
          trang hay dữ liệu nào.
        </p>
        <p className="mt-4 text-sm leading-6 text-muted-foreground">
          Hãy liên hệ quản trị để được đổi sang vai đúng với việc bạn đang làm, rồi đăng nhập lại.
        </p>
        <div className="mt-4">
          <PersonaMenuSection variant="sheet" />
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void switchAccount();
            }}
          >
            {busy ? "Đang xử lý…" : "Đổi tài khoản"}
          </Button>
        </div>
      </section>
    </main>
  );
}

function syncStaffLinkMessage(error: AuthError): string {
  switch (error.code) {
    case "staff_email_missing":
      return "Casdoor chưa có email để đối chiếu với hồ sơ nhân sự GNPF.";
    case "staff_link_ambiguous":
      return "Có nhiều hồ sơ nhân sự dùng email này. Vui lòng liên hệ admin để liên kết thủ công.";
    case "staff_link_not_found":
      return "Không tìm thấy hồ sơ nhân sự GNPF khớp với email Casdoor.";
    case "casdoor_sub_already_linked":
      return "Tài khoản Casdoor này đang được liên kết ở nơi khác. Vui lòng liên hệ admin.";
    default:
      return error.message || "Không đồng bộ được tài khoản. Vui lòng thử lại hoặc liên hệ admin.";
  }
}
